import styled from 'styled-components';

import { TaggedText } from 'components/TaggedText';
import { simpleDateAtTime } from 'lib/date';
import Activity from 'models/Activity';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import NoteMenu from './NoteMenu';

/**
 * One note: author, timestamp, body, and — for the author only — a kebab.
 *
 * No facility line: the mock doesn't show one. The API sends created_by_group and the model
 * exposes `createdByGroup`, so adding it later is a frontend-only change.
 */
export default function NoteRow({
  note,
  workflowId,
  actionId,
  isAuthor,
}: {
  note: Activity;
  workflowId: string;
  actionId: string;
  isAuthor: boolean;
}) {
  return (
    <Row data-testid='note-row'>
      <Header>
        <Author data-testid='note-author'>{note.createdBy?.fullName}</Author>
        {note.createdAt && <Timestamp>{simpleDateAtTime(note.createdAt)}</Timestamp>}
        {isAuthor && (
          <MenuCell>
            <NoteMenu note={note} workflowId={workflowId} actionId={actionId} />
          </MenuCell>
        )}
      </Header>
      <Body data-testid='note-body'>
        <TaggedText text={note.data.text ?? ''} $archived={!!note.archived} />
      </Body>
    </Row>
  );
}

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 0;

  /* The first note sits flush against the composer — its top padding would read as a gap
     twice the size of the one between notes. Uses :first-child rather than dropping the
     padding at the call site so the rule travels with the component. */
  &:first-child {
    padding-top: 0;
  }

  & + & {
    border-top: 1px solid ${colors.black10};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Author = styled(BodySmallBold)`
  font-size: 14px;
  color: ${colors.black100};
`;

const Timestamp = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black50};
`;

const MenuCell = styled.div`
  margin-left: auto;
  position: relative;
`;

// TaggedText already strikes its own text through when archived, so this wrapper only owns the
// type scale — striking here too would be the same rule in two places.
const Body = styled.div`
  font-size: 14px;
  line-height: 22px;
  color: ${colors.black100};

  /* TaggedText wraps its output in its own BodySmall, which carries the shared type scale.
     Inherit instead, so the row keeps the 14/22 the mock asks for. */
  & > * {
    font-size: inherit;
    line-height: inherit;
    color: inherit;
  }
`;
